import React from "react";
import { useWhatsAppAccountStatus } from "../hooks/useWhatsAppAccountStatus";

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (isoString) => {
  if (!isoString) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(isoString));
  } catch {
    return isoString;
  }
};

const CHECK_LABELS = {
  token_access: "Token Access",
  asset_access: "Asset Access",
  webhook_status: "Webhook",
  phone_registration_status: "Phone Registration",
};

const CHECK_PASS_VALUES = new Set(["passed", "subscribed", "registered"]);

// ─── Loading Skeleton ────────────────────────────────────────────────────────

function StatusSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-800/60 p-5 animate-pulse space-y-3">
      <div className="h-4 w-2/5 rounded bg-gray-700" />
      <div className="h-3 w-3/5 rounded bg-gray-700" />
      <div className="h-3 w-1/3 rounded bg-gray-700" />
    </div>
  );
}

// ─── Inline Error Banner ──────────────────────────────────────────────────────

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-red-500/30 bg-red-950/40 px-4 py-4">
      <span className="mt-0.5 text-red-400 text-lg">⚠</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-red-300">
          Could not load WhatsApp status
        </p>
        <p className="mt-0.5 text-xs text-red-400/80 break-words">{message}</p>
      </div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-900/40 transition-colors"
        >
          Retry
        </button>
      )}
    </div>
  );
}

// ─── Checks List ─────────────────────────────────────────────────────────────

function ChecksList({ checks }) {
  if (!checks) return null;
  const entries = Object.entries(CHECK_LABELS).map(([key, label]) => ({
    key,
    label,
    value: checks[key],
    passed: CHECK_PASS_VALUES.has(String(checks[key]).toLowerCase()),
  }));

  return (
    <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
      {entries.map(({ key, label, value, passed }) => (
        <li key={key} className="flex items-center gap-1.5">
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${
              passed ? "bg-emerald-400" : "bg-red-400"
            }`}
          />
          <span className="text-xs text-gray-400">{label}</span>
          <span
            className={`ml-auto text-xs font-medium ${
              passed ? "text-emerald-400" : "text-red-400"
            }`}
          >
            {value ?? "—"}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─── Action Button ────────────────────────────────────────────────────────────

function ActionBtn({ children, onClick, variant = "primary", ...rest }) {
  const styles = {
    primary:
      "bg-indigo-600 hover:bg-indigo-500 text-white border-transparent",
    danger:
      "bg-red-900/40 hover:bg-red-900/70 text-red-300 border-red-500/40",
    warning:
      "bg-amber-900/40 hover:bg-amber-900/70 text-amber-300 border-amber-500/40",
    ghost:
      "bg-gray-700/50 hover:bg-gray-700 text-gray-300 border-gray-600/60",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${styles[variant]}`}
      {...rest}
    >
      {children}
    </button>
  );
}

// ─── Status Views ─────────────────────────────────────────────────────────────

function NotConnectedView({ onStartSignup, isCustomerSupportEnabled, accountStatus }) {
  const hasActiveSession = accountStatus?.has_active_session;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 text-xl">
          ⚠
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-amber-900">
            {hasActiveSession ? "Incomplete WhatsApp Setup" : "WhatsApp Not Connected"}
          </p>
          <p className="mt-0.5 text-xs font-medium text-amber-800">
            {hasActiveSession 
              ? "You have an incomplete WhatsApp setup session. Resume to finish connecting your account."
              : "Connect a WhatsApp Business account to start messaging customers."}
          </p>
        </div>
      </div>
      {onStartSignup && !isCustomerSupportEnabled && (
        <div className="mt-4 flex justify-end">
          <ActionBtn variant="primary" onClick={onStartSignup}>
            {hasActiveSession ? "Resume Setup" : "Connect WhatsApp"}
          </ActionBtn>
        </div>
      )}
    </div>
  );
}

const PROCESSING_STAGES = [
  { key: "authorization_received", label: "Authorization received" },
  { key: "token_exchange",         label: "Verifying token" },
  { key: "asset_discovery",        label: "Verifying WABA & phone number" },
  { key: "webhook_subscription",   label: "Setting up webhook" },
  { key: "phone_registration",     label: "Registering phone number" },
  { key: "connection_test",        label: "Running connection test" },
  { key: "complete",               label: "Complete" },
];

function ConnectingView({ stage }) {
  const currentIdx = PROCESSING_STAGES.findIndex((s) => s.key === stage);

  return (
    <div className="rounded-2xl border border-indigo-500/30 bg-indigo-950/40 p-5">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-900/60">
          <svg
            className="h-4 w-4 animate-spin text-indigo-400"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-indigo-200">Connecting…</p>
          <p className="mt-0.5 text-xs text-indigo-400/70">
            Verifying your WhatsApp account with Meta
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-indigo-900/60 px-2.5 py-0.5 text-xs font-medium text-indigo-300 ring-1 ring-indigo-500/30">
          In Progress
        </span>
      </div>

      {/* Stage stepper */}
      <ol className="space-y-1.5 pl-1">
        {PROCESSING_STAGES.map((s, idx) => {
          const isDone    = currentIdx > idx;
          const isCurrent = currentIdx === idx;
          const isPending = currentIdx < idx;

          return (
            <li key={s.key} className="flex items-center gap-2.5">
              {/* Indicator */}
              <span
                className={[
                  "inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full",
                  isDone    ? "bg-emerald-500"                               : "",
                  isCurrent ? "ring-2 ring-indigo-400 ring-offset-1 ring-offset-indigo-950/40 bg-indigo-500" : "",
                  isPending ? "border border-gray-600 bg-transparent"        : "",
                ].join(" ")}
              >
                {isDone && (
                  <svg className="h-2 w-2 text-white" fill="currentColor" viewBox="0 0 12 12">
                    <path d="M10 3L5 8.5 2 5.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                )}
              </span>

              {/* Label */}
              <span
                className={[
                  "text-xs",
                  isDone    ? "text-emerald-400"  : "",
                  isCurrent ? "text-indigo-200 font-semibold" : "",
                  isPending ? "text-gray-600"     : "",
                ].join(" ")}
              >
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ConnectedView({ accountStatus, onDisconnect }) {
  const { account, checks, available_actions = [] } = accountStatus;

  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-950/30 p-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-900/60 text-emerald-400 text-base">
          ✓
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-200">
            {account?.verified_name || "WhatsApp Connected"}
          </p>
          {account?.display_phone_number && (
            <p className="mt-0.5 text-xs text-emerald-400/80">
              {account.display_phone_number}
            </p>
          )}
          {account?.connected_at && (
            <p className="mt-0.5 text-xs text-gray-500">
              Connected {formatDate(account.connected_at)}
            </p>
          )}
        </div>
        <span className="shrink-0 rounded-full bg-emerald-900/60 px-2.5 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-500/30">
          Connected
        </span>
      </div>

      {/* Health checks */}
      <ChecksList checks={checks} />

      {/* Actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        {available_actions.includes("test") && (
          <ActionBtn variant="ghost">Test</ActionBtn>
        )}
        {available_actions.includes("open_inbox") && (
          <ActionBtn variant="ghost">Open Inbox</ActionBtn>
        )}
        {available_actions.includes("disconnect") && (
          <ActionBtn variant="danger" onClick={onDisconnect}>
            Disconnect
          </ActionBtn>
        )}
      </div>
    </div>
  );
}

function ActionRequiredView({ accountStatus, onRetry, onDisconnect, isRetrying, retryError }) {
  const { last_error, available_actions = [] } = accountStatus;
  const canRetry = available_actions.includes("retry") || last_error?.retryable === true;

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-950/30 p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-900/60 text-amber-400 text-base">
          ⚠
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-200">Action Required</p>
          {last_error?.message && (
            <p className="mt-1 text-xs text-amber-400/80 leading-relaxed">
              {last_error.message}
            </p>
          )}
          {last_error?.code && (
            <p className="mt-0.5 text-xs text-gray-500 font-mono">
              {last_error.code}
            </p>
          )}
          {retryError && (
            <p className="mt-2 text-xs font-semibold text-red-400">
              Retry failed: {retryError}
            </p>
          )}
        </div>
        <span className="shrink-0 rounded-full bg-amber-900/60 px-2.5 py-0.5 text-xs font-medium text-amber-300 ring-1 ring-amber-500/30">
          Action Required
        </span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {canRetry && (
          <ActionBtn variant="warning" onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? "Retrying..." : "Retry Connection"}
          </ActionBtn>
        )}
        {available_actions.includes("disconnect") && (
          <ActionBtn variant="danger" onClick={onDisconnect} disabled={isRetrying}>
            Disconnect
          </ActionBtn>
        )}
      </div>
    </div>
  );
}

function FailedView({ accountStatus, onRetry, onDisconnect }) {
  const { last_error, available_actions = [] } = accountStatus;

  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-950/30 p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-900/60 text-red-400 text-base">
          ✕
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-200">Connection Failed</p>
          {last_error?.message && (
            <p className="mt-1 text-xs text-red-400/80 leading-relaxed">
              {last_error.message}
            </p>
          )}
          {last_error?.code && (
            <p className="mt-0.5 text-xs text-gray-500 font-mono">
              {last_error.code}
            </p>
          )}
        </div>
        <span className="shrink-0 rounded-full bg-red-900/60 px-2.5 py-0.5 text-xs font-medium text-red-300 ring-1 ring-red-500/30">
          Failed
        </span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {available_actions.includes("reconnect") && (
          <ActionBtn variant="primary" onClick={onRetry}>
            Reconnect
          </ActionBtn>
        )}
        {available_actions.includes("retry") && (
          <ActionBtn variant="warning" onClick={onRetry}>
            Retry
          </ActionBtn>
        )}
        {available_actions.includes("disconnect") && (
          <ActionBtn variant="danger" onClick={onDisconnect}>
            Disconnect
          </ActionBtn>
        )}
      </div>
    </div>
  );
}

function DisconnectedView({ onStartSignup }) {
  return (
    <div className="rounded-2xl border border-gray-600/40 bg-gray-800/40 p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-700/60 text-gray-500 text-base">
          ○
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-400">
            WhatsApp Disconnected
          </p>
          <p className="mt-0.5 text-xs text-gray-600">
            Your WhatsApp account was disconnected. Reconnect to resume
            messaging.
          </p>
        </div>
      </div>
      {onStartSignup && (
        <div className="mt-4 flex justify-end">
          <ActionBtn variant="primary" onClick={onStartSignup}>
            Reconnect
          </ActionBtn>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function WhatsAppAccountStatus({
  tenantId,
  onStartSignup,
  onDisconnect,
  onRetry,
  isCustomerSupportEnabled,
  // Optional props to inject shared state
  accountStatus: injectedStatus,
  loading: injectedLoading,
  error: injectedError,
  refetch: injectedRefetch,
}) {
  const localHook = useWhatsAppAccountStatus(tenantId);

  const accountStatus = injectedStatus !== undefined ? injectedStatus : localHook.accountStatus;
  const loading = injectedLoading !== undefined ? injectedLoading : localHook.loading;
  const error = injectedError !== undefined ? injectedError : localHook.error;
  const refetch = injectedRefetch !== undefined ? injectedRefetch : localHook.refetch;

  const executeRetry = localHook.executeRetry;
  const [isRetrying, setIsRetrying] = React.useState(false);
  const [retryError, setRetryError] = React.useState(null);

  const handleRetryConnection = async () => {
    setIsRetrying(true);
    setRetryError(null);
    try {
      if (executeRetry) {
        await executeRetry();
      }
      if (onRetry) {
        await onRetry();
      }
      if (refetch) {
        await refetch();
      }
    } catch (err) {
      setRetryError(err.message || "Failed to restart processing. Please try again.");
    } finally {
      setIsRetrying(false);
    }
  };

  if (loading) return <StatusSkeleton />;

  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  if (!accountStatus) return null;

  const status = accountStatus?.status;

  if (status === "not_connected") {
    return <NotConnectedView onStartSignup={onStartSignup} isCustomerSupportEnabled={isCustomerSupportEnabled} accountStatus={accountStatus} />;
  }

  if (status === "connecting") {
    return <ConnectingView stage={accountStatus.stage} />;
  }

  if (status === "connected") {
    return (
      <ConnectedView accountStatus={accountStatus} onDisconnect={onDisconnect} />
    );
  }

  if (status === "action_required") {
    return (
      <ActionRequiredView
        accountStatus={accountStatus}
        onRetry={handleRetryConnection}
        onDisconnect={onDisconnect}
        isRetrying={isRetrying}
        retryError={retryError}
      />
    );
  }

  if (status === "failed") {
    return (
      <FailedView
        accountStatus={accountStatus}
        onRetry={handleRetryConnection}
        onDisconnect={onDisconnect}
      />
    );
  }

  if (status === "disconnected") {
    return <DisconnectedView onStartSignup={onStartSignup} />;
  }

  // Unknown status — render nothing rather than crash
  return null;
}
